<template>
  <canvas ref="prog" :width="scaledWidth" :height="scaledHeight" :style="style"></canvas>
</template>

<script>
import { nearestPow2 } from '@/lib/funcs'

export default {
  name: 'progress-ring',
  props: {
    alwaysShow: {
      type: Boolean,
      default: false
    },
    percent: {
      type: Number,
      default: 0
    },
    showTrack: {
      type: Boolean,
      default: true
    },
    rgbaTrackColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.25)'
    },
    rgbaRingColor: {
      type: String,
      default: 'rgba(0, 0, 0, 1)'
    },
    width: {
      type: Number,
      default: 50
    },
    height: {
      type: Number,
      default: 50
    },
    strokeWidth: {
      type: Number,
      default: 3
    },
    dashed: {
      type: Boolean,
      default: false
    },
    segments: {
      type: Number,
      default: 0
    },
    segmentGap: {
      type: Number,
      default: 20
    },
    forcePow2: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    percent ( pct ) {
      if ( pct === 1 ) {
        this.update()
        this.onCompleteHandler()
      } else if ( pct === 0 ) {
        this.update()
      }
    }
  },
  data () {
    return {
      ctx: undefined
    }
  },
  computed: {
    scaleFactor () {
      if ( 'devicePixelRatio' in window ) {
        if ( window.devicePixelRatio > 1 ) {
          return window.devicePixelRatio
        } else if ( this.forcePow2 ) {
          return 2
        }
      }
      return 1
    },
    scaledWidth () {
      let sW = this.width * this.scaleFactor
      return this.forcePow2 ? nearestPow2( sW ) : sW
    },
    scaledHeight () {
      let sH = this.height * this.scaleFactor
      return this.forcePow2 ? nearestPow2( sH ) : sH
    },
    scaledStroke () {
      return this.strokeWidth * this.scaleFactor
    },
    x () {
      return Math.floor( this.scaledWidth / 2 )
    },
    y () {
      return Math.floor( this.scaledHeight / 2 )
    },
    radius () {
      return Math.floor( this.scaledWidth / 2 - this.scaledStroke * 2 )
    },
    circumference () {
      return 2 * Math.PI * this.radius
    },
    offset () {
      return this.dashed ? (this.segmentGap / 2) / this.radius : 0
    },
    startAngle () {
      return -Math.PI / 2 + this.offset
    },
    endAngle () {
      return Math.PI * 2
    },
    lineCap () {
      return this.percent > 0 ? 'round' : 'butt'
    },
    style () {
      let styles = {
        width: this.width + 'px',
        height: this.height + 'px'
      }
      if ( this.dashed ) {
        styles.mixBlendMode = 'multiply'
      }
      return styles
    }
  },
  methods: {
    update () {
      this.ctx.clearRect( 0, 0, this.scaledWidth, this.scaledHeight )
      if ( ( this.percent > 0 && this.$refs.prog ) || this.alwaysShow ) {
        this.ctx.lineWidth = this.scaledStroke
        if ( this.showTrack ) {
          this.ctx.lineCap = 'round'
          this.ctx.beginPath()
          this.ctx.strokeStyle = this.rgbaTrackColor
          if ( this.dashed ) {
            this.ctx.setLineDash( [ ( this.circumference / this.segments ) - this.segmentGap, this.segmentGap ] )
          }
          this.ctx.arc( this.x, this.y, this.radius, this.startAngle, this.startAngle + this.endAngle )
          this.ctx.stroke()
        }
        this.ctx.lineCap = this.lineCap
        this.ctx.strokeStyle = this.rgbaRingColor
        this.ctx.beginPath()
        if ( this.dashed ) {
          this.ctx.setLineDash( [ ( this.circumference / this.segments ) - this.segmentGap, this.segmentGap ] )
        }
        this.ctx.arc( this.x, this.y, this.radius, this.startAngle, this.startAngle + (this.endAngle * this.percent) )
        this.ctx.stroke()
      }
    },
    onCompleteHandler () {
      this.$emit('onComplete')
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.ctx = this.$refs.prog.getContext('2d')
      if ( this.alwaysShow ) {
        this.update()
      }
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/components/demos/ProgressRing.vue